import React, { useState, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getCsrfToken, loginAdmin } from "../api/authApi";
import {
  clearAuthSession,
  getPostLoginRedirectPath,
  persistUserSession,
  resolveRoleFromLoginResponse,
} from "../utils/authSession";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import GlobalFeedback from "../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../global/GlobalSuccessFlashModal";
import {
  GlobalEmailField,
  GlobalPasswordField,
} from "../../global/forms/GlobalFormFields";
import "../css/AdminLogin.css";

/** Match backend `authController` login checks */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

function validateLoginFields({ email, password }) {
  const trimmedEmail = (email ?? "").trim().toLowerCase();
  const trimmedPassword = (password ?? "").trim();

  const fieldErrors = { email: "", password: "" };

  if (!trimmedEmail) {
    fieldErrors.email = "Email is required.";
  } else if (!EMAIL_REGEX.test(trimmedEmail)) {
    fieldErrors.email = "Enter a valid email address.";
  }

  if (!trimmedPassword) {
    fieldErrors.password = "Password is required.";
  } else if (!PASSWORD_REGEX.test(trimmedPassword)) {
    fieldErrors.password =
      "Use at least 8 characters with upper & lower case, a number, and one of: @ $ ! % * ? &";
  }

  const hasFieldError = !!(fieldErrors.email || fieldErrors.password);
  return {
    ok: !hasFieldError,
    fieldErrors,
    email: trimmedEmail,
    password: trimmedPassword,
    formError: hasFieldError ? "Please update the required fields below." : "",
  };
}

function resolvePostLoginPath(location) {
  const from = location?.state?.from;
  if (typeof from === "string" && from.startsWith("/app/")) {
    return from;
  }
  const next = new URLSearchParams(location?.search || "").get("next");
  if (typeof next === "string" && next.startsWith("/app/")) {
    return next;
  }
  return "/admin/dashboard";
}

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const pendingRedirectRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    const check = validateLoginFields(formData);
    setFieldErrors(check.fieldErrors);
    if (!check.ok) {
      if (check.formError) setError(check.formError);
      return;
    }

    setLoading(true);

    try {
      clearAuthSession();
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        setFieldErrors({ email: "", password: "" });
        setError("Could not initialize secure session. Refresh the page and try again.");
        return;
      }

      const payload = {
        email: check.email,
        password: check.password,
      };

      const response = await loginAdmin(payload, csrfToken);

      const data = await response.json();

      if (response.ok) {
        const token = data.token;

        if (!token) {
          setFieldErrors({ email: "", password: "" });
          setError("Login succeeded but token was missing. Please try again.");
          return;
        }
        persistUserSession({ token, csrfToken, data });

        setSuccess("Login successful!");
        setFormData({ email: "", password: "" });
        pendingRedirectRef.current = resolvePostLoginPath(location);
      } else {
        setFieldErrors({ email: "", password: "" });
        setError(data.message || "Login failed");
      }
    } catch (error) {
      setFieldErrors({ email: "", password: "" });
      setError("Network error. Please try again.");
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Navbar />
      <main className="login-main">
        <div className="login-wrap card-reveal">
          <div className="login-panel">
            <span className="login-panel-badge">Admin</span>
            <h1 className="login-panel-title">Giri Nestham</h1>
            <p className="login-panel-desc">Sign in to manage content, tournaments, and foundation settings.</p>
            <div className="login-panel-dot" aria-hidden="true" />
          </div>

          <div className="login-form-panel">
            <div className="login-form-panel-inner">
              <div className="login-form-header">
                <div>
                  <h2 className="login-form-title">Sign in</h2>
                  <p className="login-form-subtitle">Use your admin credentials to continue.</p>
                </div>
                <Link to="/" className="login-back">← Back to site</Link>
              </div>

              <GlobalFeedback
                type="error"
                text={error}
                role="alert"
                ariaLive="assertive"
                className="login-msg error"
              />
              <GlobalSuccessFlashModal
                message={success}
                onDismiss={() => {
                  setSuccess("");
                  const path = pendingRedirectRef.current;
                  pendingRedirectRef.current = null;
                  if (path) navigate(path);
                }}
              />

              <form className="login-form" onSubmit={handleSubmit} noValidate>
                <GlobalEmailField
                  id="login-email"
                  label="Email"
                  value={formData.email}
                  onChange={(value) =>
                    {
                      setFormData((prev) => ({
                        ...prev,
                        email: value
                      }));
                      setFieldErrors((prev) => ({ ...prev, email: "" }));
                    }
                  }
                  error={fieldErrors.email}
                  placeholder="you@example.com"
                  autoComplete="email"
                />

                <GlobalPasswordField
                  id="login-password"
                  label="Password"
                  value={formData.password}
                  onChange={(value) =>
                    {
                      setFormData((prev) => ({
                        ...prev,
                        password: value
                      }));
                      setFieldErrors((prev) => ({ ...prev, password: "" }));
                    }
                  }
                  error={fieldErrors.password}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  visibleLabel={{ show: "Show password", hide: "Hide password" }}
                />

                <div className="login-actions">
                  <button type="submit" className="login-btn" disabled={loading}>
                    {loading ? "Signing in…" : "Sign in"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer organizationName="Giri Nestham" />
    </div>
  );
};

export default AdminLogin;
